import React, { useEffect, useState } from "react";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Trial, TrialIngredient, TrialParameter } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import { bakerPctNum } from "./trialUtils";
import { calcFlatMetrics } from "../../../utils/recipeMetrics";
import { t, dateLocale } from "../../../i18n/locale";

/**
 * TrialTablet - Simplified tablet interface for production floor
 * Used during trial execution for parameter entry and observations
 */
const TrialTablet = () => {
  savePathToLocalStorage();

  const [trials, setTrials] = useState<Trial[]>([]);
  const [selectedTrial, setSelectedTrial] = useState<Trial | null>(null);
  const [ingredients, setIngredients] = useState<TrialIngredient[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Parameter entry state
  const [paramForm, setParamForm] = useState({
    masa_zamjesa_kg: "",
    temp_zamjes_cel: "",
    ph_starter: "",
    t_start_mijesanja: "",
    t_kraj_mijesanja: "",
    t_kraj_masa: "",
    t_poc_zavrsna: "",
    t_kraj_zavrsna: "",
    t_start_pecenje: "",
    t_kraj_pecenje: "",
    temp_pecenje_cel: "",
    spec_vol_lkg: "",
    napomena: "",
  });

  // Observation entry state
  const [observations, setObservations] = useState<{
    phase: string;
    timestamp: string;
    note: string;
  }[]>([]);
  const [newObservation, setNewObservation] = useState({ phase: "mijesanje", note: "" });

  useEffect(() => {
    loadTrials();
  }, []);

  const loadTrials = async () => {
    setLoading(true);
    try {
      // Load only confirmed trials for today's execution
      const data = await mesApi.getTrials({ status: "confirmed" });
      setTrials(data);
    } catch (err: any) {
      toast.error(t("Greska pri ucitavanju: {message}", { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTrial = async (trial: Trial) => {
    setSelectedTrial(trial);
    setLoading(true);
    try {
      // Load trial ingredients
      const [ingredientsData, paramsData] = await Promise.all([
        mesApi.getTrialIngredients(trial.ID),
        mesApi.getTrialParameters(trial.ID),
      ]);
      setIngredients(ingredientsData);

      // Load existing parameters if any
      if (paramsData && paramsData.length > 0) {
        const p = paramsData[0];
        setParamForm({
          masa_zamjesa_kg: String(p.DoughMassKg || ""),
          temp_zamjes_cel: String(p.DoughTempC || ""),
          ph_starter: String(p.StarterPh || ""),
          t_start_mijesanja: (p as any).t_start_mijesanja || "",
          t_kraj_mijesanja: (p as any).t_kraj_mijesanja || "",
          t_kraj_masa: (p as any).t_kraj_masa || "",
          t_poc_zavrsna: (p as any).t_poc_zavrsna || "",
          t_kraj_zavrsna: (p as any).t_kraj_zavrsna || "",
          t_start_pecenje: (p as any).t_start_pecenje || "",
          t_kraj_pecenje: (p as any).t_kraj_pecenje || "",
          temp_pecenje_cel: String((p as any).temp_pecenje_cel || ""),
          spec_vol_lkg: String((p as any).spec_vol_lkg || ""),
          napomena: p.Notes || "",
        });
      } else {
        // Reset form
        setParamForm({
          masa_zamjesa_kg: String(trial.DoughMassKg || ""),
          temp_zamjes_cel: "",
          ph_starter: "",
          t_start_mijesanja: "",
          t_kraj_mijesanja: "",
          t_kraj_masa: "",
          t_poc_zavrsna: "",
          t_kraj_zavrsna: "",
          t_start_pecenje: "",
          t_kraj_pecenje: "",
          temp_pecenje_cel: "",
          spec_vol_lkg: "",
          napomena: "",
        });
      }
    } catch (err: any) {
      toast.error(t("Greska pri ucitavanju detalja: {message}", { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveParameters = async () => {
    if (!selectedTrial) return;

    setSaving(true);
    try {
      await mesApi.createTrialParameter({
        Trial: selectedTrial.ID,
        DoughMassKg: paramForm.masa_zamjesa_kg ? parseFloat(paramForm.masa_zamjesa_kg) : undefined,
        DoughTempC: paramForm.temp_zamjes_cel ? parseFloat(paramForm.temp_zamjes_cel) : undefined,
        StarterPh: paramForm.ph_starter ? parseFloat(paramForm.ph_starter) : undefined,
        Notes: paramForm.napomena || undefined,
        // Extended fields would be saved here
      });
      toast.success(t("Parametri spremljeni!"));
    } catch (err: any) {
      toast.error(t("Greska pri spremanju: {message}", { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  const handleAddObservation = () => {
    if (!newObservation.note.trim()) return;

    setObservations([
      ...observations,
      {
        phase: newObservation.phase,
        timestamp: new Date().toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" }),
        note: newObservation.note,
      },
    ]);
    setNewObservation({ ...newObservation, note: "" });
    toast.info(t("Opazanje dodano"));
  };

  const handleSetCurrentTime = (field: keyof typeof paramForm) => {
    const now = new Date().toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" });
    setParamForm({ ...paramForm, [field]: now });
  };

  const calculateFlourMass = () => {
    const flourIngredients = ingredients.filter(
      (i) => {
        const t = i.IngredientType?.toLowerCase() || "";
        return t.includes("brasno") || t === "flour" || i.IngredientName?.toLowerCase().includes("brasno");
      }
    );
    return flourIngredients.reduce((sum, i) => sum + bakerPctNum(i.BakerPercentage), 0);
  };

  // Hidracija usklađena s mes-pekara logikom (recipeMetrics.ts):
  // faza 1 bez "voda 2", ukupna s "voda 2"; brašno uključuje enzime.
  const getMetrics = () =>
    calcFlatMetrics(
      ingredients.map((i) => ({
        type: i.IngredientType,
        name: i.IngredientName,
        pct: i.BakerPercentage,
      }))
    );
  const fmtHydration = (value: number, flour: number) =>
    flour > 0 ? `${value.toFixed(1)}%` : "-";

  // Render trial selection
  if (!selectedTrial) {
    return (
      <div className="trial-tablet">
        <div className="tablet-header">
          <h1>{t("PROBA - Tablet")}</h1>
          <p>{t("Odaberite probu za izvrsenje")}</p>
        </div>

        {loading ? (
          <div className="tablet-loading">{t("Ucitavanje...")}</div>
        ) : trials.length === 0 ? (
          <div className="tablet-empty">
            <div className="empty-icon">🧪</div>
            <p>{t("Nema potvrdenih proba za izvrsenje")}</p>
          </div>
        ) : (
          <div className="trial-grid">
            {trials.map((trial) => (
              <div
                key={trial.ID}
                className="trial-card"
                onClick={() => handleSelectTrial(trial)}
              >
                <div className="trial-card-header">
                  <span className="trial-code">{trial.TrialCode || `P-${trial.ID}`}</span>
                  <span className={`trial-status status-${trial.Status}`}>
                    {trial.Status === "confirmed" ? t("Potvrdjeno") : trial.Status}
                  </span>
                </div>
                <h3>{trial.TrialCode || t("Bez naziva")}</h3>
                <div className="trial-meta">
                  <span>{t("Datum:")} {new Date(trial.TrialDate).toLocaleDateString(dateLocale())}</span>
                  <span>{t("Masa:")} {trial.DoughMassKg || "-"} kg</span>
                </div>
                {trial.Purpose && <p className="trial-purpose">{trial.Purpose}</p>}
              </div>
            ))}
          </div>
        )}

        <ToastContainer autoClose={3000} position="bottom-center" />
      </div>
    );
  }

  // Render trial execution view
  return (
    <div className="trial-tablet executing">
      {/* Header */}
      <div className="tablet-header">
        <button className="back-btn" onClick={() => setSelectedTrial(null)}>
          {t("← Nazad")}
        </button>
        <div className="header-info">
          <h1>{selectedTrial.TrialCode || `PROBA ${selectedTrial.ID}`}</h1>
          <span>{selectedTrial.TrialCode}</span>
        </div>
        <div className="header-meta">
          {(() => {
            const m = getMetrics();
            return (
              <>
                <span className="meta-item">{t("Masa:")} <strong>{selectedTrial.DoughMassKg || "-"} kg</strong></span>
                <span className="meta-item">{t("Hidracija faza 1:")} <strong>{fmtHydration(m.hydration_phase_1, m.flour_total)}</strong></span>
                <span className="meta-item">{t("Ukupna hidracija:")} <strong>{fmtHydration(m.total_hydration, m.flour_total)}</strong></span>
              </>
            );
          })()}
        </div>
      </div>

      <div className="tablet-body">
        {/* Recipe/Ingredients Panel */}
        <div className="tablet-panel ingredients-panel">
          <h2>{t("Receptura")}</h2>
          <div className="mes-table-wrap">
            <table className="ingredients-table">
            <thead>
              <tr>
                <th>{t("Sirovina")}</th>
                <th>{t("%")}</th>
                <th>{t("Masa (kg)")}</th>
              </tr>
            </thead>
            <tbody>
              {ingredients.map((ing) => {
                const flourTotal = calculateFlourMass();
                const pek = bakerPctNum(ing.BakerPercentage);
                const doughKg = bakerPctNum(selectedTrial.DoughMassKg);
                const massKg = flourTotal > 0 && doughKg
                  ? (pek / flourTotal) * (doughKg / (1 + (100 - flourTotal) / 100))
                  : 0;
                return (
                  <tr key={ing.ID} className={ing.IsNewIngredient ? "new-ingredient" : ""}>
                    <td>
                      {ing.IngredientName}
                      {ing.IsNewIngredient && <span className="new-badge">{t("NOVO")}</span>}
                    </td>
                    <td>{pek.toFixed(1)}%</td>
                    <td>{massKg.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        {/* Parameter Entry Panel */}
        <div className="tablet-panel params-panel">
          <h2>{t("Parametri")}</h2>

          <div className="param-section">
            <h3>{t("Mikser")}</h3>
            <div className="param-grid">
              <div className="param-field">
                <label>{t("Start mijesanja")}</label>
                <div className="input-with-btn">
                  <input
                    type="time"
                    value={paramForm.t_start_mijesanja}
                    onChange={(e) => setParamForm({ ...paramForm, t_start_mijesanja: e.target.value })}
                  />
                  <button onClick={() => handleSetCurrentTime("t_start_mijesanja")}>{t("Sad")}</button>
                </div>
              </div>
              <div className="param-field">
                <label>{t("Kraj mijesanja")}</label>
                <div className="input-with-btn">
                  <input
                    type="time"
                    value={paramForm.t_kraj_mijesanja}
                    onChange={(e) => setParamForm({ ...paramForm, t_kraj_mijesanja: e.target.value })}
                  />
                  <button onClick={() => handleSetCurrentTime("t_kraj_mijesanja")}>{t("Sad")}</button>
                </div>
              </div>
              <div className="param-field">
                <label>{t("Masa zamjesa (kg)")}</label>
                <input
                  type="number"
                  step="0.1"
                  value={paramForm.masa_zamjesa_kg}
                  onChange={(e) => setParamForm({ ...paramForm, masa_zamjesa_kg: e.target.value })}
                />
              </div>
              <div className="param-field">
                <label>{t("Temp (°C)")}</label>
                <input
                  type="number"
                  step="0.1"
                  value={paramForm.temp_zamjes_cel}
                  onChange={(e) => setParamForm({ ...paramForm, temp_zamjes_cel: e.target.value })}
                />
              </div>
              <div className="param-field">
                <label>{t("pH starter")}</label>
                <input
                  type="number"
                  step="0.01"
                  value={paramForm.ph_starter}
                  onChange={(e) => setParamForm({ ...paramForm, ph_starter: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="param-section">
            <h3>{t("Fermentacija")}</h3>
            <div className="param-grid">
              <div className="param-field">
                <label>{t("Kraj mase")}</label>
                <div className="input-with-btn">
                  <input
                    type="time"
                    value={paramForm.t_kraj_masa}
                    onChange={(e) => setParamForm({ ...paramForm, t_kraj_masa: e.target.value })}
                  />
                  <button onClick={() => handleSetCurrentTime("t_kraj_masa")}>{t("Sad")}</button>
                </div>
              </div>
              <div className="param-field">
                <label>{t("Pocetak zavrsne")}</label>
                <div className="input-with-btn">
                  <input
                    type="time"
                    value={paramForm.t_poc_zavrsna}
                    onChange={(e) => setParamForm({ ...paramForm, t_poc_zavrsna: e.target.value })}
                  />
                  <button onClick={() => handleSetCurrentTime("t_poc_zavrsna")}>{t("Sad")}</button>
                </div>
              </div>
              <div className="param-field">
                <label>{t("Kraj zavrsne")}</label>
                <div className="input-with-btn">
                  <input
                    type="time"
                    value={paramForm.t_kraj_zavrsna}
                    onChange={(e) => setParamForm({ ...paramForm, t_kraj_zavrsna: e.target.value })}
                  />
                  <button onClick={() => handleSetCurrentTime("t_kraj_zavrsna")}>{t("Sad")}</button>
                </div>
              </div>
            </div>
          </div>

          <div className="param-section">
            <h3>{t("Pecenje")}</h3>
            <div className="param-grid">
              <div className="param-field">
                <label>{t("Start pecenja")}</label>
                <div className="input-with-btn">
                  <input
                    type="time"
                    value={paramForm.t_start_pecenje}
                    onChange={(e) => setParamForm({ ...paramForm, t_start_pecenje: e.target.value })}
                  />
                  <button onClick={() => handleSetCurrentTime("t_start_pecenje")}>{t("Sad")}</button>
                </div>
              </div>
              <div className="param-field">
                <label>{t("Kraj pecenja")}</label>
                <div className="input-with-btn">
                  <input
                    type="time"
                    value={paramForm.t_kraj_pecenje}
                    onChange={(e) => setParamForm({ ...paramForm, t_kraj_pecenje: e.target.value })}
                  />
                  <button onClick={() => handleSetCurrentTime("t_kraj_pecenje")}>{t("Sad")}</button>
                </div>
              </div>
              <div className="param-field">
                <label>{t("Temp (°C)")}</label>
                <input
                  type="number"
                  step="1"
                  value={paramForm.temp_pecenje_cel}
                  onChange={(e) => setParamForm({ ...paramForm, temp_pecenje_cel: e.target.value })}
                />
              </div>
              <div className="param-field">
                <label>{t("Spec. vol (L/kg)")}</label>
                <input
                  type="number"
                  step="0.001"
                  value={paramForm.spec_vol_lkg}
                  onChange={(e) => setParamForm({ ...paramForm, spec_vol_lkg: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="param-field full-width">
            <label>{t("Napomena")}</label>
            <textarea
              value={paramForm.napomena}
              onChange={(e) => setParamForm({ ...paramForm, napomena: e.target.value })}
              placeholder={t("Dodatne napomene...")}
              rows={2}
            />
          </div>

          <button
            className="save-btn"
            onClick={handleSaveParameters}
            disabled={saving}
          >
            {saving ? t("Spremanje...") : t("Spremi parametri")}
          </button>
        </div>

        {/* Observations Panel */}
        <div className="tablet-panel observations-panel">
          <h2>{t("Opazanja")}</h2>

          <div className="observation-input">
            <select
              value={newObservation.phase}
              onChange={(e) => setNewObservation({ ...newObservation, phase: e.target.value })}
            >
              <option value="mijesanje">{t("Mijesanje")}</option>
              <option value="fermentacija">{t("Fermentacija")}</option>
              <option value="oblikovanje">{t("Oblikovanje")}</option>
              <option value="pecenje">{t("Pecenje")}</option>
              <option value="hladenje">{t("Hladenje")}</option>
              <option value="kvaliteta">{t("Kvaliteta")}</option>
            </select>
            <input
              type="text"
              placeholder={t("Unesite opazanje...")}
              value={newObservation.note}
              onChange={(e) => setNewObservation({ ...newObservation, note: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && handleAddObservation()}
            />
            <button onClick={handleAddObservation}>{t("Dodaj")}</button>
          </div>

          <div className="observations-list">
            {observations.length === 0 ? (
              <p className="empty-obs">{t("Nema opazanja")}</p>
            ) : (
              observations.map((obs, idx) => (
                <div key={idx} className={`observation-item phase-${obs.phase}`}>
                  <span className="obs-time">{obs.timestamp}</span>
                  <span className="obs-phase">{obs.phase}</span>
                  <span className="obs-note">{obs.note}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <ToastContainer autoClose={3000} position="bottom-center" />
    </div>
  );
};

export default TrialTablet;
